import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Breadcrumbs,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Drawer,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
  Skeleton,
  Chip
} from '@mui/material';
import { FilterList as FilterIcon, Close as CloseIcon, ClearAll as ClearAllIcon } from '@mui/icons-material';
import SidebarFilters from './components/SidebarFilters';
import ProductCard from '../../common/ProductCard';
import SEO from '../../common/SEO';
import { apiList, invokeGetApi } from '../../../services/apiServices';
import { useLocation, useNavigate } from 'react-router-dom';
import { products as dummyProducts } from '../../../config/products';

const categoryDisplayNames = {
  '': 'All Products',
  'microsoft': 'Microsoft Licenses',
  'windows': 'Windows Operating Systems',
  'windows-7': 'Windows 7 Operating System',
  'windows-8': 'Windows 8 Operating System',
  'windows-8-1': 'Windows 8.1 Operating System',
  'windows-10': 'Windows 10 Operating System',
  'windows-11': 'Windows 11 Operating System',
  'ms-office': 'Microsoft Office Suites',
  'office': 'Microsoft Office Suites',
  'servers': 'Windows Server Editions',
  'windows-server': 'Windows Server Editions',
  'sql-server': 'Microsoft SQL Server',
  'visual-studio': 'Microsoft Visual Studio',
  'adobe': 'Adobe Creative Software',
  'others': 'Specialized Software (CAD, Corel, Tally, Antivirus)',
  'antivirus': 'Antivirus & Cybersecurity Software',
  'auto-desk': 'Autodesk (AutoCAD & Revit)',
  'autodesk': 'Autodesk (AutoCAD & Revit)',
  'coreldraw': 'CorelDraw Graphics Suite',
  'tally': 'Tally Prime ERP & GST',
  'combo': 'Software Combo Bundles',
};

const ShopPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  // Extract ?category= parameter from URL
  const activeCategoryParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get('category') || '').trim().toLowerCase();
  }, [location.search]);

  const [filters, setFilters] = useState({
    category: activeCategoryParam,
    priceRange: [0, 10000],
    volumes: [],
    sortBy: 'relevance'
  });

  // Re-sync filter when URL param changes
  useEffect(() => {
    setFilters(prev => ({ ...prev, category: activeCategoryParam }));
  }, [activeCategoryParam]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Fetch products from backend or fallback to products catalog
  useEffect(() => {
    const fetchAllProducts = async () => {
      setLoading(true);
      try {
        const response = await invokeGetApi(apiList.getAllProducts);
        let allFetched = [];
        if (response?.data?.products && Array.isArray(response.data.products)) {
          allFetched = response.data.products;
        } else if (Array.isArray(response?.data)) {
          allFetched = response.data;
        }
        setAllProducts(allFetched.length > 0 ? allFetched : dummyProducts);
      } catch (error) {
        setAllProducts(dummyProducts);
      } finally {
        setLoading(false);
      }
    };
    fetchAllProducts();
  }, []);

  // Filter products client-side instantaneously
  useEffect(() => {
    let filtered = [...allProducts];

    // 1. Category / Subcategory Filter
    if (filters.category) {
      const target = String(filters.category).toLowerCase().trim();

      filtered = filtered.filter(p => {
        const pCat = String(p.category || '').toLowerCase();
        const pParent = String(p.parent_category || '').toLowerCase();
        const pSub = String(p.subcategory || '').toLowerCase();
        const pTags = Array.isArray(p.tags) ? p.tags.map(t => String(t).toLowerCase()) : [];
        const pName = String(p.name || '').toLowerCase();
        const pSlug = String(p.slug || '').toLowerCase();

        // Exact match on subcategory or category slug
        if (pSub === target || pCat === target || pParent === target) return true;
        if (pTags.includes(target)) return true;

        // Specific subcategory logic
        if (target === 'microsoft') {
          return pParent === 'microsoft' || pTags.includes('microsoft') || pName.includes('microsoft') || pCat.includes('windows') || pCat.includes('office') || pCat.includes('server') || pCat.includes('visual studio');
        }
        if (target === 'windows') {
          return pCat.includes('windows') || pTags.includes('windows') || pName.includes('windows 1') || pName.includes('windows 7') || pName.includes('windows 8');
        }
        if (target === 'windows-7') {
          return pSub === 'windows-7' || pTags.includes('windows-7') || pName.includes('windows 7');
        }
        if (target === 'windows-8' || target === 'windows-8-1') {
          return pSub.includes('windows-8') || pTags.includes('windows-8') || pName.includes('windows 8');
        }
        if (target === 'windows-10') {
          return pSub === 'windows-10' || pTags.includes('windows-10') || pName.includes('windows 10');
        }
        if (target === 'windows-11') {
          return pSub === 'windows-11' || pTags.includes('windows-11') || pName.includes('windows 11');
        }
        if (target === 'ms-office' || target === 'office' || target === 'microsoft-office') {
          return pCat.includes('office') || pTags.includes('ms-office') || pTags.includes('office') || pName.includes('office');
        }
        if (target === 'servers' || target === 'server' || target === 'windows-server') {
          return (pCat.includes('server') || pTags.includes('servers') || pName.includes('server')) && !pName.includes('sql');
        }
        if (target === 'sql-server' || target === 'sql') {
          return pSub.includes('sql') || pTags.includes('sql-server') || pName.includes('sql');
        }
        if (target === 'visual-studio') {
          return pSub.includes('visual-studio') || pTags.includes('visual-studio') || pName.includes('visual studio');
        }
        if (target === 'adobe') {
          return pCat.includes('adobe') || pParent === 'adobe' || pTags.includes('adobe') || pName.includes('adobe') || pName.includes('photoshop') || pName.includes('acrobat');
        }
        if (target === 'others') {
          return pCat.includes('others') || pParent === 'others' || pTags.includes('others');
        }
        if (target === 'antivirus' || target === 'security') {
          return pCat.includes('antivirus') || pSub.includes('antivirus') || pTags.includes('antivirus') || pName.includes('antivirus') || pName.includes('quick heal') || pName.includes('kaspersky') || pName.includes('norton') || pName.includes('mcafee') || pName.includes('bitdefender');
        }
        if (target === 'auto-desk' || target === 'autodesk') {
          return pSub.includes('auto-desk') || pTags.includes('auto-desk') || pName.includes('autodesk') || pName.includes('autocad') || pName.includes('revit');
        }
        if (target === 'coreldraw') {
          return pSub.includes('coreldraw') || pTags.includes('coreldraw') || pName.includes('coreldraw');
        }
        if (target === 'tally') {
          return pSub.includes('tally') || pTags.includes('tally') || pName.includes('tally');
        }
        if (target === 'combo' || target === 'combo-deals') {
          return pCat.includes('combo') || pParent === 'combo' || pTags.includes('combo') || pName.includes('combo') || pName.includes('bundle') || pName.includes('pack');
        }

        return false;
      });
    }

    // 2. Price Range Filter
    if (filters.priceRange) {
      filtered = filtered.filter(p => {
        const pPrice = Number(p.price || 0);
        return pPrice >= filters.priceRange[0] && pPrice <= filters.priceRange[1];
      });
    }

    // 3. Dynamic Contextual Sub-Filter Matching (Antivirus brands, Autodesk tools, Windows/Office editions, etc.)
    if (filters.volumes && filters.volumes.length > 0) {
      filtered = filtered.filter(p => {
        const pName = String(p.name || '').toLowerCase();
        const pBrand = String(p.brand || '').toLowerCase();
        const pTags = Array.isArray(p.tags) ? p.tags.map(t => String(t).toLowerCase()) : [];
        const pSub = String(p.subcategory || '').toLowerCase();
        const pSlug = String(p.slug || '').toLowerCase();
        const pWeight = String(p.weight || '').toLowerCase();
        const pBadge = String(p.badge || '').toLowerCase();

        return filters.volumes.some(v => {
          const cleanV = v.toLowerCase().trim();
          return (
            pName.includes(cleanV) ||
            pBrand.includes(cleanV) ||
            pTags.includes(cleanV) ||
            pSub.includes(cleanV) ||
            pSlug.includes(cleanV) ||
            pWeight.includes(cleanV) ||
            pBadge.includes(cleanV)
          );
        });
      });
    }

    // 4. Sorting
    if (filters.sortBy === 'price-low') {
      filtered.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
    } else if (filters.sortBy === 'price-high') {
      filtered.sort((a, b) => Number(b.price || 0) - Number(a.price || 0));
    } else if (filters.sortBy === 'rating') {
      filtered.sort((a, b) => Number(b.rating || 0) - Number(a.rating || 0));
    }

    setProducts(filtered);
  }, [allProducts, filters]);

  const currentCategoryLabel = categoryDisplayNames[filters.category] ||
    (filters.category ? String(filters.category).replace('-', ' ').toUpperCase() : 'ALL SOFTWARE LICENSES');

  return (
    <Box sx={{ py: 4, minHeight: '80vh' }}>
      <SEO
        title={`${currentCategoryLabel} - AR Infotech Genuine Licenses`}
        description="Browse genuine Microsoft Windows operating systems, Office suites, Server editions, Adobe, AutoCAD, CorelDRAW, and Tally licenses. 100% genuine with instant digital delivery."
      />
      <Container maxWidth="lg">
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
          <Link underline="hover" color="inherit" href="/" sx={{ fontSize: '0.88rem' }}>Home</Link>
          <Link underline="hover" color="inherit" onClick={() => { setFilters(prev => ({ ...prev, category: '' })); navigate('/shop'); }} sx={{ fontSize: '0.88rem', cursor: 'pointer' }}>
            Shop
          </Link>
          {filters.category && (
            <Typography color="text.primary" sx={{ fontSize: '0.88rem', fontWeight: 600 }}>
              {currentCategoryLabel}
            </Typography>
          )}
        </Breadcrumbs>

        <Grid container spacing={4}>
          {/* Desktop Sidebar */}
          {!isMobile && (
            <Grid item md={3}>
              <SidebarFilters filters={filters} setFilters={setFilters} />
            </Grid>
          )}

          {/* Product Grid Area */}
          <Grid item xs={12} md={9}>
            {/* Header banner */}
            <Box
              sx={{
                p: { xs: 2.5, sm: 3.5 },
                borderRadius: 3,
                bgcolor: '#F8FAFC',
                border: '1px solid #E2E8F0',
                mb: 4,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2
              }}
            >
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A', mb: 0.5 }}>
                  {currentCategoryLabel}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Showing <strong>{products.length}</strong> genuine software license{products.length !== 1 ? 's' : ''}
                </Typography>
              </Box>

              {filters.category && (
                <Chip
                  label={`Filter: ${filters.category}`}
                  onDelete={() => {
                    setFilters(prev => ({ ...prev, category: '' }));
                    navigate('/shop');
                  }}
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>

            {/* Mobile Filter Trigger & Sort */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              {isMobile && (
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={handleDrawerToggle}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                >
                  Filters
                </Button>
              )}

              <Box sx={{ ml: 'auto', minWidth: 160 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={filters.sortBy}
                    label="Sort By"
                    onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="relevance">Featured & Relevant</MenuItem>
                    <MenuItem value="price-low">Price: Low to High</MenuItem>
                    <MenuItem value="price-high">Price: High to Low</MenuItem>
                    <MenuItem value="rating">Highest Rated</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Loading Skeleton */}
            {loading ? (
              <Grid container spacing={2.5}>
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <Grid item xs={6} sm={4} key={i}>
                    <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
                    <Skeleton variant="text" sx={{ mt: 1 }} />
                    <Skeleton variant="text" width="60%" />
                  </Grid>
                ))}
              </Grid>
            ) : products.length === 0 ? (
              /* Empty state */
              <Box sx={{ textAlign: 'center', py: 10, px: 2, bgcolor: '#F8FAFC', borderRadius: 3, border: '1px dashed #CBD5E1' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#334155' }}>
                  No Products Found in this Category
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Try resetting your price range or choosing another software category.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<ClearAllIcon />}
                  onClick={() => {
                    setFilters({ category: '', priceRange: [0, 10000], volumes: [], sortBy: 'relevance' });
                    navigate('/shop');
                  }}
                  sx={{ bgcolor: '#0E6ADA', textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
                >
                  Show All Products
                </Button>
              </Box>
            ) : (
              /* Products Grid */
              <Grid container spacing={2.5}>
                {products.map((product) => (
                  <Grid item xs={6} sm={4} key={product.id}>
                    <ProductCard product={product} selectedVolumes={filters.volumes} />
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>
      </Container>

      {/* Mobile Filters Drawer */}
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{ '& .MuiDrawer-paper': { width: 300, p: 3 } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Filters</Typography>
          <IconButton onClick={handleDrawerToggle}><CloseIcon /></IconButton>
        </Box>
        <SidebarFilters
          filters={filters}
          setFilters={(f) => {
            setFilters(f);
            setMobileOpen(false);
          }}
        />
      </Drawer>
    </Box>
  );
};

export default ShopPage;
